/**
 * Facelift Project API
 *
 * Stores facelift project data (overview + page inventory) in the KV store.
 * Uses shared apiClient for all server communication.
 *
 * Keys (all under the template's namespace):
 *   {templateId}:facelift:project  →  FaceliftProjectData
 *   {templateId}:facelift:pages    →  FaceliftPageItem[]
 */

import { apiFetch } from "./apiClient";

/* ================================================================
   Types
   ================================================================ */

export type FaceliftChangeType = "keep" | "design" | "content" | "both" | "new" | "remove";
export type FaceliftPriority = "high" | "medium" | "low";
export type FaceliftStatus = "todo" | "in-progress" | "review" | "done";
export type FaceliftPageType =
  | "home" | "about" | "services" | "service"
  | "contact" | "blog" | "post" | "gallery"
  | "product" | "faq" | "landing" | "other";

export interface FaceliftPageItem {
  id: string;
  name: string;
  currentUrl: string;
  pageType: FaceliftPageType;
  changeType: FaceliftChangeType;
  priority: FaceliftPriority | null;
  status: FaceliftStatus;
  designNotes: string;
  contentNotes: string;
  generalNotes: string;
  seoTitle?: string;
  seoDescription?: string;
  keywords?: string;
  assignedDesigner?: string;
  assignedContentWriter?: string;
  /** Optional: links this page to a site tree node ID */
  siteTreeNodeId?: string;
  order: number;
  createdAt: number;
  updatedAt: number;
}

export interface FaceliftProjectData {
  /* Existing site */
  siteName: string;
  currentUrl: string;
  platform: string;
  hosting: string;
  projectScope: "design" | "content" | "both";
  projectGoal: string;

  /* Site login credentials */
  siteLoginUrl: string;
  siteLoginUser: string;
  siteLoginPass: string;
  siteLoginNotes: string;

  /* Domain */
  domainRegistrar: string;
  domainExpiry: string;
  domainNotes: string;

  /* Business */
  businessDescription: string;
  targetAudience: string;
  competitors: string;
  toneOfVoice: string;

  /* Design direction */
  keepFromBrand: string;
  designChanges: string;
  referenceLinks: string;
  designNotes: string;

  /* Content */
  contentTone: string;
  contentLanguages: string;
  contentNotes: string;

  /* Technical */
  integrations: string;
  technicalNotes: string;

  /* Designer guidelines */
  designerPreferredColors: string;
  designerRefUrl1: string;
  designerRefUrl2: string;
  designerRefFeedback: string;
  designerLogoLink: string;
  designerExtraMaterial: string;
  designerExistingSite: string;

  /* Contact & Social */
  contactPhoneSeo: string;
  contactPhoneDirect: string;
  contactWhatsapp: string;
  contactHours: string;
  contactAddress: string;
  contactEmail: string;
  contactGmb: string;
  contactFacebook: string;
  contactInstagram: string;
  contactYoutube: string;
  contactLinkedin: string;
  contactTiktok: string;
  contactOther: string;

  /* Client notes & meeting */
  clientConstraints: string;
  meetingSummary: string;
  extraLinks: string;

  updatedAt: number;
}

/* ================================================================
   Visual config maps (used by both pages)
   ================================================================ */

export const CHANGE_TYPE_CONFIG: Record<FaceliftChangeType, {
  label: string; icon: string; bg: string; text: string; border: string;
}> = {
  keep:    { label: "\u05DC\u05DC\u05D0 \u05E9\u05D9\u05E0\u05D5\u05D9",      icon: "\u2714\uFE0F", bg: "#f3f4f6", text: "#4b5563", border: "#d1d5db" },
  design:  { label: "\u05E2\u05D9\u05E6\u05D5\u05D1",          icon: "\uD83C\uDFA8", bg: "#ede9fe", text: "#6d28d9", border: "#c4b5fd" },
  content: { label: "\u05EA\u05D5\u05DB\u05DF",           icon: "\u270F\uFE0F", bg: "#d1fae5", text: "#065f46", border: "#6ee7b7" },
  both:    { label: "\u05E2\u05D9\u05E6\u05D5\u05D1 + \u05EA\u05D5\u05DB\u05DF",   icon: "\uD83D\uDD04", bg: "#fef3c7", text: "#92400e", border: "#fbbf24" },
  new:     { label: "\u05E2\u05DE\u05D5\u05D3 \u05D7\u05D3\u05E9",       icon: "\u2728", bg: "#dbeafe", text: "#1e40af", border: "#93c5fd" },
  remove:  { label: "\u05D4\u05E1\u05E8\u05D4",           icon: "\uD83D\uDDD1\uFE0F", bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
};

export const PRIORITY_CONFIG: Record<FaceliftPriority, {
  label: string; bg: string; text: string; border: string;
}> = {
  high:   { label: "\u05D2\u05D1\u05D5\u05D4\u05D4",   bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
  medium: { label: "\u05D1\u05D9\u05E0\u05D5\u05E0\u05D9\u05EA",  bg: "#fef3c7", text: "#92400e", border: "#fbbf24" },
  low:    { label: "\u05E0\u05DE\u05D5\u05DB\u05D4",   bg: "#dbeafe", text: "#1e40af", border: "#93c5fd" },
};

export const STATUS_CONFIG: Record<FaceliftStatus, {
  label: string; bg: string; text: string; border: string; dot: string;
}> = {
  "todo":        { label: "\u05DC\u05D0 \u05D4\u05EA\u05D7\u05D9\u05DC",  bg: "#f3f4f6", text: "#6b7280", border: "#d1d5db", dot: "#9ca3af" },
  "in-progress": { label: "\u05D1\u05EA\u05D4\u05DC\u05D9\u05DA",    bg: "#dbeafe", text: "#1e40af", border: "#93c5fd", dot: "#3b82f6" },
  "review":      { label: "\u05D1\u05D1\u05D3\u05D9\u05E7\u05D4",    bg: "#fef3c7", text: "#92400e", border: "#fbbf24", dot: "#f59e0b" },
  "done":        { label: "\u05D4\u05D5\u05E9\u05DC\u05DD",     bg: "#d1fae5", text: "#065f46", border: "#6ee7b7", dot: "#10b981" },
};

export const PAGE_TYPE_LABELS: Record<FaceliftPageType, string> = {
  home:     "\u05D3\u05E3 \u05D1\u05D9\u05EA",
  about:    "\u05D0\u05D5\u05D3\u05D5\u05EA",
  services: "\u05E9\u05D9\u05E8\u05D5\u05EA\u05D9\u05DD",
  service:  "\u05E9\u05D9\u05E8\u05D5\u05EA",
  contact:  "\u05E6\u05D5\u05E8 \u05E7\u05E9\u05E8",
  blog:     "\u05D1\u05DC\u05D5\u05D2",
  post:     "\u05E4\u05D5\u05E1\u05D8",
  gallery:  "\u05D2\u05DC\u05E8\u05D9\u05D4",
  product:  "\u05DE\u05D5\u05E6\u05E8",
  faq:      "\u05E9\u05D0\u05DC\u05D5\u05EA \u05E0\u05E4\u05D5\u05E6\u05D5\u05EA",
  landing:  "\u05D3\u05E3 \u05E0\u05D7\u05D9\u05EA\u05D4",
  other:    "\u05D0\u05D7\u05E8",
};

/* ================================================================
   Default data
   ================================================================ */

export function getDefaultProjectData(): FaceliftProjectData {
  return {
    siteName: "",
    currentUrl: "",
    platform: "",
    hosting: "",
    projectScope: "both",
    projectGoal: "",
    /* Site login credentials */
    siteLoginUrl: "",
    siteLoginUser: "",
    siteLoginPass: "",
    siteLoginNotes: "",
    /* Domain */
    domainRegistrar: "",
    domainExpiry: "",
    domainNotes: "",
    businessDescription: "",
    targetAudience: "",
    competitors: "",
    toneOfVoice: "",
    keepFromBrand: "",
    designChanges: "",
    referenceLinks: "",
    designNotes: "",
    contentTone: "",
    contentLanguages: "",
    contentNotes: "",
    integrations: "",
    technicalNotes: "",
    /* Designer guidelines */
    designerPreferredColors: "",
    designerRefUrl1: "",
    designerRefUrl2: "",
    designerRefFeedback: "",
    designerLogoLink: "",
    designerExtraMaterial: "",
    designerExistingSite: "",
    /* Contact & social */
    contactPhoneSeo: "",
    contactPhoneDirect: "",
    contactWhatsapp: "",
    contactHours: "",
    contactAddress: "",
    contactEmail: "",
    contactGmb: "",
    contactFacebook: "",
    contactInstagram: "",
    contactYoutube: "",
    contactLinkedin: "",
    contactTiktok: "",
    contactOther: "",
    /* Client notes & meeting */
    clientConstraints: "",
    meetingSummary: "",
    extraLinks: "",
    updatedAt: Date.now(),
  };
}

/* ================================================================
   Keys
   ================================================================ */

function projectKey(templateId: string) { return `${templateId}:facelift:project`; }
function pagesKey(templateId: string)   { return `${templateId}:facelift:pages`; }

/* ================================================================
   Project overview API
   ================================================================ */

export async function getFaceliftProject(templateId: string): Promise<FaceliftProjectData | null> {
  try {
    // Use dedicated per-template endpoint — avoids downloading ALL content
    const data = await apiFetch(`/content/${encodeURIComponent(templateId)}`);
    const raw: Record<string, any> = data.content || {};
    const value = raw[projectKey(templateId)];
    if (value && typeof value === "object") return value as FaceliftProjectData;
    return null;
  } catch {
    return null;
  }
}

export async function saveFaceliftProject(
  templateId: string,
  updates: Partial<FaceliftProjectData>,
): Promise<FaceliftProjectData> {
  const existing = await getFaceliftProject(templateId);
  const merged: FaceliftProjectData = {
    ...(existing || getDefaultProjectData()),
    ...updates,
    updatedAt: Date.now(),
  };
  await apiFetch("/content/bulk", {
    method: "PUT",
    body: JSON.stringify({ overrides: { [projectKey(templateId)]: merged } }),
  });
  return merged;
}

/* ================================================================
   Pages API
   ================================================================ */

export async function getFaceliftPages(templateId: string): Promise<FaceliftPageItem[]> {
  try {
    // Use dedicated per-template endpoint — avoids downloading ALL content
    const data = await apiFetch(`/content/${encodeURIComponent(templateId)}`);
    const raw: Record<string, any> = data.content || {};
    const value = raw[pagesKey(templateId)];
    if (Array.isArray(value)) return value as FaceliftPageItem[];
    return [];
  } catch {
    return [];
  }
}

export async function saveFaceliftPages(
  templateId: string,
  pages: FaceliftPageItem[],
): Promise<void> {
  await apiFetch("/content/bulk", {
    method: "PUT",
    body: JSON.stringify({ overrides: { [pagesKey(templateId)]: pages } }),
  });
}

export async function addFaceliftPage(
  templateId: string,
  pageData: Omit<FaceliftPageItem, "id" | "createdAt" | "updatedAt" | "order">,
): Promise<FaceliftPageItem> {
  const pages = await getFaceliftPages(templateId);
  const now = Date.now();
  const newPage: FaceliftPageItem = {
    ...pageData,
    id: "fp-" + now.toString(36) + "-" + Math.random().toString(36).slice(2, 6),
    order: pages.length,
    createdAt: now,
    updatedAt: now,
  };
  await saveFaceliftPages(templateId, [...pages, newPage]);
  return newPage;
}

export async function updateFaceliftPage(
  templateId: string,
  pageId: string,
  updates: Partial<Omit<FaceliftPageItem, "id" | "createdAt">>,
): Promise<FaceliftPageItem> {
  const pages = await getFaceliftPages(templateId);
  const idx = pages.findIndex((p) => p.id === pageId);
  if (idx === -1) throw new Error(`Page not found: ${pageId}`);
  const updated: FaceliftPageItem = { ...pages[idx], ...updates, updatedAt: Date.now() };
  const newPages = [...pages];
  newPages[idx] = updated;
  await saveFaceliftPages(templateId, newPages);
  return updated;
}

export async function deleteFaceliftPage(templateId: string, pageId: string): Promise<void> {
  const pages = await getFaceliftPages(templateId);
  await saveFaceliftPages(templateId, pages.filter((p) => p.id !== pageId));
}

export async function reorderFaceliftPages(
  templateId: string,
  orderedIds: string[],
): Promise<void> {
  const pages = await getFaceliftPages(templateId);
  const reordered = orderedIds
    .map((id, idx) => {
      const p = pages.find((x) => x.id === id);
      return p ? { ...p, order: idx } : null;
    })
    .filter(Boolean) as FaceliftPageItem[];
  // Append any pages not in orderedIds at the end
  const missing = pages.filter((p) => !orderedIds.includes(p.id));
  await saveFaceliftPages(templateId, [...reordered, ...missing]);
}

/* ================================================================
   Completion helper (for dashboard badge)
   ================================================================ */

export function calcFaceliftCompletion(pages: FaceliftPageItem[]): {
  total: number; filled: number; percentage: number;
} {
  const relevant = pages.filter((p) => p.changeType !== "keep" && p.changeType !== "remove");
  const done = relevant.filter((p) => p.status === "done").length;
  const total = relevant.length;
  return { total, filled: done, percentage: total > 0 ? Math.round((done / total) * 100) : 0 };
}